<template>
  <div class="forum-wrapper">
    <v-container fluid class="forum-thread-detail-container">
      <!-- Thread Header -->
      <v-row>
        <v-col cols="12" class="mb-4">
          <div v-if="loadingThread">
            <v-skeleton-loader type="heading" class="mb-4" />
            <v-skeleton-loader type="text" />
          </div>
          <div v-else class="thread-header">
            <h2 class="text-h5 font-weight-bold">{{ thread.title }}</h2>
            <div class="thread-info text-caption text-muted" v-if="thread.created_at">
              Created by {{ thread.created_by?.username }} on {{ formatDate(thread.created_at) }}
            </div>
            <v-btn small color="primary" class="new-post-button" @click="showNewPostDialog = true">
              New Post
            </v-btn>
          </div>
        </v-col>
      </v-row>

      <!-- Forum Posts -->
      <v-row class="mt-4">
        <v-col cols="12">
          <h4 class="text-h6 font-weight-bold mb-3">Posts</h4>
          <div v-if="loadingPosts">
            <v-skeleton-loader
              v-for="n in perPage"
              :key="n"
              type="list-item-two-line"
              class="mb-3"
            />
          </div>
          <div v-else>
            <div v-if="paginatedPosts.length === 0" class="text-subtitle-2 text-muted">
              No posts in this thread yet.
            </div>
            <div v-else>
              <div
                v-for="p in paginatedPosts"
                :key="p.id"
                :id="`post-${p.id}`"
                class="post-wrapper"
              >
                <PostItem
                  :post="p"
                  :threadId="threadId"
                  :highlightId="highlightId"
                  :highlightType="highlightType"
                  @reply="openReplyModal"
                  @like="toggleLike"
                  @edit="goEditPost"
                  @delete="goDeletePost"
                />
              </div>
            </div>
          </div>
        </v-col>
      </v-row>

      <!-- Pagination -->
      <v-row class="my-4" justify="center">
        <v-col cols="12" class="text-center">
          <div>Page {{ page }} of {{ totalPages }}</div>
        </v-col>
      </v-row>
      <v-row class="my-4" justify="center">
        <v-col cols="12" sm="auto">
          <v-pagination
            v-if="totalPages > 1"
            v-model="page"
            :length="totalPages"
            :total-visible="7"
            :prev-icon="''"
            :next-icon="''"
          >
            <template #prev="{ onClick, disabled }">
              <SvgIcon
                :path="mdiChevronLeft"
                size="24"
                style="color:rgba(0,0,0,0.38);vertical-align:middle;transform:translateY(12px);cursor:pointer;"
                @click="!disabled && onClick($event)"
                :class="{ 'opacity-38': disabled }"
              />
            </template>
            <template #next="{ onClick, disabled }">
              <SvgIcon
                :path="mdiChevronRight"
                size="24"
                style="color:rgba(0,0,0,0.38);vertical-align:middle;transform:translateY(12px);cursor:pointer;"
                @click="!disabled && onClick($event)"
                :class="{ 'opacity-38': disabled }"
              />
            </template>
          </v-pagination>
        </v-col>
      </v-row>

      <!-- New Post Dialog -->
      <v-dialog v-model="showNewPostDialog" max-width="600px">
        <v-card class="quill-card">
          <v-card-title class="text-h6 font-weight-bold">Create New Post</v-card-title>
          <v-card-text>
            <!-- CHANGED: Removed fixed height, replaced with "editor-area" class -->
            <div ref="quillNewPost" class="quill-editor editor-area"></div>
            <small class="d-block mt-2 text-muted">
              Tip: Type <strong>@</strong> to mention someone.
            </small>
          </v-card-text>
          <v-card-actions>
            <v-btn color="primary" @click="submitNewPost">Submit Post</v-btn>
            <v-btn text @click="showNewPostDialog = false">Cancel</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Reply Dialog -->
      <v-dialog v-model="showReplyModal" max-width="800px">
        <v-card class="quill-card">
          <v-card-title class="text-h6 font-weight-bold">Reply to Post</v-card-title>
          <v-card-text>
            <!-- CHANGED: Also use "editor-area" for the reply editor -->
            <div :key="replyEditorKey" ref="quillReplyPost" class="quill-editor editor-area"></div>
          </v-card-text>
          <v-card-actions>
            <v-btn color="primary" @click="submitReply">Submit Reply</v-btn>
            <v-btn text @click="closeReplyModal">Cancel</v-btn>
          </v-card-actions>
          <small class="d-block ml-5 mb-3 text-muted">
            Tip: Type <strong>@</strong> to mention someone.
          </small>
        </v-card>
      </v-dialog>
    </v-container>
  </div>
</template>

<script>
import api from '@/plugins/axios';
import PostItem from "@/views/ForumPostItem.vue";
import Quill from "quill";
import "quill/dist/quill.snow.css";
import { imageHandler } from '@/utils/quillImageUploader';
import "quill-mention/dist/quill.mention.css";
import { mdiChevronLeft, mdiChevronRight } from "@mdi/js";

export default {
  name: "ForumThreadDetail",
  components: { PostItem },
  data() {
    return {
      threadId: null,
      thread: {},
      posts: [],
      page: 1,
      perPage: 10,
      showReplyModal: false,
      replyToId: null,
      quillNew: null,
      quillReply: null,
      highlightId: null,
      highlightType: "forum_like",
      replyEditorKey: 0,
      showNewPostDialog: false,
      mdiChevronLeft,
      mdiChevronRight,
      loadingThread: true,
      loadingPosts:   true,
    };
  },
  computed: {
    totalPages() {
      return this.posts.length ? Math.ceil(this.posts.length / this.perPage) : 1;
    },
    paginatedPosts() {
      const start = (this.page - 1) * this.perPage;
      return this.posts.slice(start, start + this.perPage);
    },
  },
  watch: {
    page() {
      this.$nextTick(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
      });
    },
    "$route.query.highlight": {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.highlightId = newVal;
          if (this.posts && this.posts.length) {
            this.setPageForHighlight();
          }
        }
      },
    },
    posts(newPosts) {
      if (this.highlightId && newPosts.length) {
        this.setPageForHighlight();
      }
    },
  },
  methods: {
    formatDate(dt) {
      return new Date(dt).toLocaleString();
    },
    async fetchThread() {
      this.loadingThread = true;
      try {
        const res = await api.get(`/api/forum-threads/${this.threadId}/`);
        this.thread = res.data;
      } catch (err) {
        console.error("Error fetching thread:", err);
        if (err.response && (err.response.status === 404 || err.response.status === 403)) {
          alert("Access forbidden or thread not found.");
          this.$router.push("/forum");
        }
      } finally {
        this.loadingThread = false;
      }
    },
    async fetchPosts() {
      this.loadingPosts = true;
      try {
        const res = await api.get('/api/forum-posts/', {
          params: { thread: this.threadId },
        });
        const all = res.data;
        const mapById = {};
        all.forEach((p) => {
          p.replies = [];
          mapById[p.id] = p;
        });
        const topLevel = [];
        all.forEach((p) => {
          if (!p.parent) {
            topLevel.push(p);
          } else if (mapById[p.parent]) {
            mapById[p.parent].replies.push(p);
          }
        });
        topLevel.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
        this.posts = topLevel;
        if (this.highlightId) {
          this.setPageForHighlight();
        }
      } catch (err) {
        console.error("Error fetching posts:", err);
      } finally {
        this.loadingPosts = false;
      }
    },
    findInReplies(replies, targetId) {
      if (!replies || !replies.length) return false;
      for (const reply of replies) {
        if (reply.id === targetId) return true;
        if (this.findInReplies(reply.replies, targetId)) return true;
      }
      return false;
    },
    setPageForHighlight() {
      const targetId = parseInt(this.highlightId, 10);
      let topLevelIndex = -1;
      for (let i = 0; i < this.posts.length; i++) {
        const post = this.posts[i];
        if (post.id === targetId || this.findInReplies(post.replies, targetId)) {
          topLevelIndex = i;
          break;
        }
      }
      if (topLevelIndex !== -1) {
        this.page = Math.floor(topLevelIndex / this.perPage) + 1;
        this.$nextTick(() => {
          this.scrollToHighlight();
        });
      }
    },
    initNewQuill() {
      const toolbarOptions = [
        [{ font: [] }],
        [{ header: [1, 2, 3, false] }],
        ["bold", "italic", "underline", "strike"],
        [{ align: [] }],
        [{ list: "ordered" }, { list: "bullet" }],
        [{ script: "sub" }, { script: "super" }],
        [{ indent: "-1" }, { indent: "+1" }],
        ["blockquote", "code-block"],
        ["link", "image", "video"],
        ["clean"],
      ];
      const modulesObj = {
        toolbar: {
          container: toolbarOptions,
          handlers: {
            image: function() { imageHandler(this.quill); },
            link: function(value) {
              if (value) {
                const href = prompt('Enter the URL');
                if (href) {
                  const formatted = href.startsWith('http://') || href.startsWith('https://')
                    ? href
                    : `https://${href}`;
                  this.quill.format('link', formatted);
                }
              } else {
                this.quill.format('link', false);
              }
            }
          }
        },
        mention: {
          mentionDenotationChars: ["@"],
          renderDenotationChar: false,
          source: (searchTerm, renderList) => {
            if (!searchTerm) {
              renderList([], searchTerm);
              return;
            }
            api
              .get('/api/forum/mention-suggestions/', {
                params: { query: searchTerm },
              })
              .then((resp) => {
                renderList(resp.data, searchTerm);
              })
              .catch((err) => {
                console.error("Mention fetch error:", err);
                renderList([], searchTerm);
              });
          },
          renderItem: (item) => {
            return item.full_name || item.value;
          },
          onSelect: (item, insertItem) => {
            const mentionItem = { ...item, value: item.full_name || item.value };
            insertItem(mentionItem);
          },
        },
      };
      if (!this.$refs.quillNewPost) return;
      this.quillNew = new Quill(this.$refs.quillNewPost, {
        theme: "snow",
        modules: modulesObj,
      });
    },
    initReplyQuill() {
      const toolbarOptions = [
        [{ font: [] }],
        [{ header: [1, 2, 3, false] }],
        ["bold", "italic", "underline", "strike"],
        [{ align: [] }],
        [{ list: "ordered" }, { list: "bullet" }],
        [{ script: "sub" }, { script: "super" }],
        [{ indent: "-1" }, { indent: "+1" }],
        ["blockquote", "code-block"],
        ["link", "image", "video"],
        ["clean"],
      ];
      const modulesObj = {
        toolbar: {
          container: toolbarOptions,
          handlers: {
            image: function() { imageHandler(this.quill); },
            link: function(value) {
              if (value) {
                const href = prompt('Enter the URL');
                if (href) {
                  const formatted = href.startsWith('http://') || href.startsWith('https://')
                    ? href
                    : `https://${href}`;
                  this.quill.format('link', formatted);
                }
              } else {
                this.quill.format('link', false);
              }
            }
          }
        },
        mention: {
          mentionDenotationChars: ["@"],
          renderDenotationChar: false,
          source: (searchTerm, renderList) => {
            if (!searchTerm) {
              renderList([], searchTerm);
              return;
            }
            api
              .get('/api/forum/mention-suggestions/', {
                params: { query: searchTerm },
              })
              .then((resp) => {
                renderList(resp.data, searchTerm);
              })
              .catch((err) => {
                console.error("Mention fetch error:", err);
                renderList([], searchTerm);
              });
          },
          renderItem: (item) => {
            return item.full_name || item.value;
          },
          onSelect: (item, insertItem) => {
            const mentionItem = { ...item, value: item.full_name || item.value };
            insertItem(mentionItem);
          },
        },
      };
      if (!this.$refs.quillReplyPost) return;
      this.quillReply = new Quill(this.$refs.quillReplyPost, {
        theme: "snow",
        modules: modulesObj,
      });
    },
    async submitNewPost() {
      if (!this.quillNew) return;
      let contentHtml = this.quillNew.root.innerHTML;
      // Auto-prefix any href that is missing http:// or https://
      contentHtml = contentHtml.replace(/href="(?!https?:\/\/)([^"]+)"/g, (match, p1) => `href="https://${p1}"`);
      if (!contentHtml || contentHtml === "<p><br></p>") return;
      try {
        const resp = await api.post(
          '/api/forum-posts/',
          { thread: this.threadId, parent: null, content: contentHtml }
        );
        const newId = resp.data.id;
        this.quillNew.root.innerHTML = "";
        this.showNewPostDialog = false;
        await this.fetchPosts();
        this.page = this.totalPages;
        this.highlightId = newId;
        this.$nextTick(() => {
          this.scrollToHighlight();
        });
      } catch (error) {
        console.error("Error submitting new post:", error);
      }
    },
    openReplyModal(parentId) {
      this.replyToId = parentId;
      this.replyEditorKey++;
      this.showReplyModal = true;
      this.$nextTick(() => {
        this.initReplyQuill();
        if (this.quillReply) {
          this.quillReply.root.innerHTML = "";
        }
      });
    },
    async submitReply() {
      if (!this.quillReply) return;
      let contentHtml = this.quillReply.root.innerHTML;
      // Auto-prefix any href that is missing http:// or https://
      contentHtml = contentHtml.replace(/href="(?!https?:\/\/)([^"]+)"/g, (match, p1) => `href="https://${p1}"`);
      if (!contentHtml || contentHtml === "<p><br></p>") return;
      try {
        const resp = await api.post(
          '/api/forum-posts/',
          { thread: this.threadId, parent: this.replyToId, content: contentHtml }
        );
        const newId = resp.data.id;
        this.closeReplyModal();
        await this.fetchPosts();
        this.highlightId = newId;
        this.$nextTick(() => {
          this.setPageForHighlight();
        });
      } catch (error) {
        console.error("Error submitting reply:", error);
      }
    },
    closeReplyModal() {
      this.showReplyModal = false;
      this.quillReply = null;
    },
    async toggleLike(post) {
      try {
        const { data } = await api.post('/api/forum/post/like/', { post_id: post.id });
        post.likes_count = data.like_count;
        post.like_list = data.like_list || [];
      } catch (err) {
        console.error('Error toggling like:', err);
      }
    },
    goEditPost(postId) {
      this.$router.push(`/forum/post/${postId}/edit`);
    },
    goDeletePost(postId) {
      this.$router.push(`/forum/post/${postId}/delete`);
    },
    scrollToHighlight() {
      if (!this.highlightId) return;
      const el = document.getElementById("post-" + this.highlightId);
      if (el) {
        // scroll to the element
        const top = el.getBoundingClientRect().top + window.pageYOffset - 120;
        window.scrollTo({ top, behavior: "smooth" });
        // briefly flash the background, then fade out
        el.classList.add("highlighted");
        setTimeout(() => {
          el.classList.remove("highlighted");
        }, 500);
      }
    },
  },
  async created() {
    this.threadId = +this.$route.params.threadId;
    this.highlightId = this.$route.query.highlight || null;
    await this.fetchThread();
    await this.fetchPosts();
  },
  mounted() {
    // Watch the dialog open for new post => init Quill
    this.$watch(
      () => this.showNewPostDialog,
      (newVal) => {
        if (newVal) {
          this.$nextTick(() => {
            this.initNewQuill();
          });
        }
      }
    );
    setTimeout(() => {
      window.dispatchEvent(new Event("resize"));
    }, 200);
  },
};
</script>

<style scoped>
.forum-wrapper {
  overflow: visible !important;
}
.forum-thread-detail-container {
  background: linear-gradient(to bottom, #fafafa, #e7effa, #d2e3f5);
  min-height: 100vh;
  padding: 20px;
  position: relative;
}
.thread-header {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
/* We'll keep .quill-editor but also add .editor-area for a bigger, flexible area */
.quill-editor.editor-area {
  min-height: 300px;
  background-color: #fff;
  padding: 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
}

.quill-card {
  overflow: visible !important;
}

/* Constrain images within Quill editor content */
:deep(.quill-editor .ql-editor img) {
  max-width: 100% !important;
  height: auto !important;
  max-height: 250px !important;
}

.new-post-button {
  display: inline-flex;
  width: auto;
  max-width: none;
  font-size: 0.85rem;
  padding: 6px 12px;
  align-self: flex-start;
}
.post-wrapper {
  background-color: #fff;
}
.post-wrapper.highlighted {
  background-color: #e0f7ff;
  transition: background-color 2s ease;
}
.v-pagination .v-pagination__navigation {
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>

<style scoped>
/* allow Quill link tooltip to overflow and stay on top */
:deep(.ql-tooltip) {
  z-index: 10000 !important;
  overflow: visible !important;
}
/* ensure the editor container lets tooltips be visible outside */
.quill-editor.editor-area {
  overflow: visible !important;
}
/* allow Quill container to show overflowed tooltips */
.quill-editor.editor-area .ql-container {
  overflow: visible !important;
}
/* allow Quill tooltip in dialogs for new post and reply */
.v-dialog > .v-card,
.v-dialog .editor-area {
  overflow: visible !important;
}
/* Added for dialog content and tooltip positioning */
:deep(.v-dialog__content) {
  overflow: visible !important;
}

</style>  